import { useMemo, Fragment } from 'react'
import { Banner } from 'wss3-forge'
import {
  Warning20Regular,
  Info20Regular,
  Lightbulb20Regular
} from '@fluentui/react-icons'

interface MarkdownRendererProps {
  content: string
}

interface ContentBlock {
  type: 'html' | 'callout'
  content: string
  calloutType?: 'warning' | 'info' | 'tip'
}

export function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const blocks = useMemo(() => {
    return parseMarkdownBlocks(content)
  }, [content])

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code)
  }

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.classList.contains('copy-button')) {
      const pre = target.closest('.code-block')?.querySelector('pre')
      if (pre) {
        copyCode(pre.textContent || '')
        target.textContent = 'Copied!'
        setTimeout(() => {
          target.textContent = 'Copy'
        }, 2000)
      }
    }
  }

  return (
    <div className="markdown-content animate-fadeIn" onClick={handleClick}>
      {blocks.map((block, index) => (
        <Fragment key={index}>
          {block.type === 'callout' ? (
            <Banner
              variant={block.calloutType === 'tip' ? 'success' : block.calloutType}
              title={block.calloutType === 'warning' ? 'Warning' : block.calloutType === 'info' ? 'Info' : 'Tip'}
              icon={
                block.calloutType === 'warning' ? <Warning20Regular /> :
                block.calloutType === 'info' ? <Info20Regular /> :
                <Lightbulb20Regular />
              }
              style={{ margin: '1.5rem 0' }}
            >
              <span dangerouslySetInnerHTML={{ __html: convertInlineMarkdown(block.content) }} />
            </Banner>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: block.content }} />
          )}
        </Fragment>
      ))}
    </div>
  )
}

function parseMarkdownBlocks(markdown: string): ContentBlock[] {
  const blocks: ContentBlock[] = []
  const calloutRegex = /:::(warning|info|tip)\n([\s\S]*?):::/g

  let lastIndex = 0
  let match

  while ((match = calloutRegex.exec(markdown)) !== null) {
    // Add HTML content before this callout
    if (match.index > lastIndex) {
      const htmlContent = convertMarkdownToHtml(markdown.slice(lastIndex, match.index))
      if (htmlContent.trim()) {
        blocks.push({ type: 'html', content: htmlContent })
      }
    }

    // Add the callout block
    blocks.push({
      type: 'callout',
      calloutType: match[1] as 'warning' | 'info' | 'tip',
      content: match[2].trim()
    })

    lastIndex = match.index + match[0].length
  }

  // Add remaining content after last callout
  if (lastIndex < markdown.length) {
    const htmlContent = convertMarkdownToHtml(markdown.slice(lastIndex))
    if (htmlContent.trim()) {
      blocks.push({ type: 'html', content: htmlContent })
    }
  }

  return blocks
}

function convertTables(markdown: string): string {
  // Match table blocks: lines starting with |
  const tableRegex = /(?:^|\n)((?:\|[^\n]+\|\n?)+)/g

  return markdown.replace(tableRegex, (match, tableBlock) => {
    const lines = tableBlock.trim().split('\n').filter((l: string) => l.trim())
    if (lines.length < 2) return match

    // Parse header row
    const headerCells = lines[0]
      .split('|')
      .map((c: string) => c.trim())
      .filter((c: string) => c)

    // Skip separator row (|---|---|)
    const dataStartIndex = lines[1].includes('---') ? 2 : 1

    // Parse data rows
    const dataRows = lines.slice(dataStartIndex).map((line: string) =>
      line
        .split('|')
        .map((c: string) => c.trim())
        .filter((c: string) => c)
    )

    // Build HTML table
    let tableHtml = '<table><thead><tr>'
    headerCells.forEach((cell: string) => {
      tableHtml += `<th>${convertInlineMarkdown(cell)}</th>`
    })
    tableHtml += '</tr></thead><tbody>'

    dataRows.forEach((row: string[]) => {
      tableHtml += '<tr>'
      row.forEach((cell: string) => {
        tableHtml += `<td>${convertInlineMarkdown(cell)}</td>`
      })
      tableHtml += '</tr>'
    })

    tableHtml += '</tbody></table>'
    return '\n' + tableHtml + '\n'
  })
}

function convertMarkdownToHtml(markdown: string): string {
  let html = markdown

  // Framework blocks - show ALL with label (no filtering)
  html = html.replace(
    /:::framework (\w+)\n([\s\S]*?):::/g,
    (_, fw, content) => {
      return `<div class="framework-block"><span class="framework-label">${fw.toUpperCase()}</span><div class="framework-content">${convertInlineMarkdown(content)}</div></div>`
    }
  )

  // Tables - must be processed before code blocks
  html = convertTables(html)

  // Code blocks with language and optional filename
  html = html.replace(
    /```(\w+)?(?:\s+filename="([^"]+)")?\n([\s\S]*?)```/g,
    (_, lang, filename, code) => {
      const header = filename
        ? `<div class="code-header"><span>${filename}</span><button class="copy-button">Copy</button></div>`
        : lang
          ? `<div class="code-header"><span>${lang}</span><button class="copy-button">Copy</button></div>`
          : ''
      return `<div class="code-block">${header}<pre><code class="language-${lang || 'text'}">${escapeHtml(code.trim())}</code></pre></div>`
    }
  )

  // Images (process before paragraphs so they don't get wrapped in <p>)
  html = html.replace(
    /^!\[([^\]]*)\]\(([^)]+)\)$/gm,
    '<figure class="image-block"><img src="$2" alt="$1" loading="lazy" /><figcaption>$1</figcaption></figure>'
  )
  // Inline images
  html = html.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img src="$2" alt="$1" loading="lazy" class="inline-image" />'
  )

  // Headings with IDs for TOC (process from h4 to h1, most specific first)
  html = html.replace(/^#### (.+)$/gm, (_, text) => {
    const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
    return `<h4 id="${id}">${text}</h4>`
  })
  html = html.replace(/^### (.+)$/gm, (_, text) => {
    const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
    return `<h3 id="${id}">${text}</h3>`
  })
  html = html.replace(/^## (.+)$/gm, (_, text) => {
    const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
    return `<h2 id="${id}">${text}</h2>`
  })
  html = html.replace(/^# (.+)$/gm, (_, text) => {
    const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
    return `<h1 id="${id}">${text}</h1>`
  })

  // Horizontal rule
  html = html.replace(/^---$/gm, '<hr />')

  // Lists
  html = html.replace(/^(\s*)- (.+)$/gm, '$1<li>$2</li>')
  html = html.replace(/^(\s*)\d+\. (.+)$/gm, '$1<li>$2</li>')

  // Wrap consecutive li in ul/ol
  html = html.replace(/(<li>[\s\S]*?<\/li>)+/g, match => `<ul>${match}</ul>`)

  // Inline elements (but not images since we already processed them)
  html = convertInlineMarkdown(html)

  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p>')

  if (!html.startsWith('<')) {
    html = '<p>' + html + '</p>'
  }

  return html
}

function convertInlineMarkdown(text: string): string {
  return text
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    // Blockquotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}
